import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const fmtGBP = (pence) =>
    `£${((pence || 0) / 100).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : "";
const todayIso = () => new Date().toISOString().slice(0, 10);

const Expenses = () => {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Add dialog
    const [addOpen, setAddOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [date, setDate] = useState(todayIso());
    const [category, setCategory] = useState("Fuel");
    const [description, setDescription] = useState("");
    const [amount, setAmount] = useState("");
    const [notes, setNotes] = useState("");

    const load = () => {
        setLoading(true);
        Promise.all([api.get("/expenses"), api.get("/expenses/summary")])
            .then(([e, s]) => {
                setItems(e.data);
                setSummary(s.data);
            })
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        load();
        api.get("/expenses/categories").then((r) => setCategories(r.data.categories));
    }, []);

    const openAdd = () => {
        setDate(todayIso());
        setCategory("Fuel");
        setDescription("");
        setAmount("");
        setNotes("");
        setAddOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const num = parseFloat(amount);
        if (!num || num <= 0) return toast.error("Enter an amount");
        if (!description.trim()) return toast.error("Add a description");
        setSaving(true);
        try {
            await api.post("/expenses", {
                date,
                category,
                description: description.trim(),
                amount_gbp: num,
                notes: notes.trim() || null,
            });
            toast.success("Expense logged");
            setAddOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await api.delete(`/expenses/${id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const total = useMemo(() => summary?.total_pence || 0, [summary]);

    return (
        <div className="space-y-8" data-testid="expenses-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-black text-orange-600">
                        Expenses · {summary?.year || new Date().getFullYear()}
                    </p>
                    <h1 className="font-display font-black text-4xl sm:text-5xl uppercase leading-none">
                        Cash out
                    </h1>
                </div>
                <button
                    onClick={openAdd}
                    className="dh-btn"
                    data-testid="expenses-add-btn"
                >
                    <Plus size={16} /> Log expense
                </button>
            </div>

            {/* Total hero */}
            <div className="border-2 border-neutral-900 bg-neutral-900 text-white px-6 py-7">
                <p className="text-[10px] uppercase tracking-widest font-black text-orange-400">
                    Total spent this year
                </p>
                <p
                    className="font-display font-black text-5xl sm:text-6xl leading-none mt-2"
                    data-testid="expenses-total"
                >
                    {fmtGBP(total)}
                </p>
            </div>

            {/* By category */}
            {summary && summary.by_category.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                        By category
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {summary.by_category.map((r) => (
                            <div
                                key={r.category}
                                className="border-2 border-neutral-900 bg-white px-4 py-3"
                                data-testid={`expenses-cat-${r.category}`}
                            >
                                <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500">
                                    {r.category}
                                </p>
                                <p className="font-display font-black text-2xl leading-none mt-1">
                                    {fmtGBP(r.amount_pence)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* List */}
            <div>
                <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                    All expenses
                </p>
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-neutral-500">
                        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="border-2 border-dashed border-neutral-300 px-5 py-10 text-center text-neutral-500">
                        No expenses logged yet. Log fuel, parts and van costs as they happen.
                    </div>
                ) : (
                    <div className="border-2 border-neutral-900 bg-white">
                        {items.map((it) => (
                            <div
                                key={it.id}
                                className="flex items-center gap-3 px-4 py-3 border-t border-neutral-200 first:border-t-0"
                                data-testid={`expense-row-${it.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold truncate">
                                        {it.description}{" "}
                                        <span className="text-xs uppercase font-black text-orange-600 tracking-wider">
                                            {it.category}
                                        </span>
                                    </p>
                                    <p className="text-xs text-neutral-500">
                                        {fmtDate(it.date)}
                                        {it.notes ? ` · ${it.notes}` : ""}
                                    </p>
                                </div>
                                <span className="font-black">{fmtGBP(it.amount_pence)}</span>
                                <button
                                    onClick={() => remove(it.id)}
                                    className="dh-btn"
                                    data-variant="ghost"
                                    aria-label="Delete"
                                    data-testid={`expense-del-${it.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent
                    className="bg-white border-2 border-neutral-900 rounded-none max-w-md"
                    data-testid="expense-add-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-xl uppercase">
                            Log expense
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="mt-1 w-full border-2 border-neutral-900 h-11 px-3"
                                    data-testid="expense-date"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                    Category
                                </label>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger
                                        className="mt-1 border-2 border-neutral-900 rounded-none h-11"
                                        data-testid="expense-category"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {c}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                Description
                            </label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Diesel · fill up Bosch"
                                className="mt-1 w-full border-2 border-neutral-900 h-11 px-3"
                                data-testid="expense-desc"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                Amount (£)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="mt-1 w-full border-2 border-neutral-900 h-11 px-3"
                                data-testid="expense-amount"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                Notes
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                className="mt-1 w-full border-2 border-neutral-900 p-2"
                                data-testid="expense-notes"
                            />
                        </div>
                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                            <button
                                type="button"
                                className="dh-btn w-full sm:w-auto bg-white"
                                onClick={() => setAddOpen(false)}
                                disabled={saving}
                                data-testid="expense-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="dh-btn w-full sm:w-auto"
                                disabled={saving}
                                data-testid="expense-save"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Expenses;
